import { defineStore } from 'pinia'
import { bookingService } from '@/api/bookingService'
import type { GuestInfo, Booking, DateAvailability, TableBookingResponse } from '@/types/booking.types'
import { dateUtils } from '@/utils/dateUtils'

// Helper function for API date formatting (ISO format for API calls)
function formatApiDate(date: Date): string {
  return date.toISOString().split('T')[0]
}

// Cache duration in milliseconds (15 minutes)
const CACHE_DURATION = 15 * 60 * 1000

export const useBookingStore = defineStore('booking', {
  state: () => ({
    placeId: 'default', // Default fallback
    countryCode: 'tw', // Default fallback
    selectedDate: null as Date | null,
    selectedTime: null as string | null,
    partySize: 2,
    maxPartySize: 20, // Default max party size, will be updated from API
    availableTimes: [] as string[],
    availableDates: [] as DateAvailability[],
    guestInfo: null as GuestInfo | null,
    temporaryBooking: null as Booking | null,
    expirationTime: null as Date | null,
    isLoading: false,
    loadingAvailableDates: false,
    error: null as string | null,
    // Updated caching structure for all party sizes
    masterAvailabilityCache: {
      data: {} as Record<number, DateAvailability[]>,
      timestamp: 0
    },
    partialAvailableDates: [] as DateAvailability[],
    isIncrementalLoading: false,
    isSubmittingBooking: false, // Add flag to prevent multiple submissions
    lastStatusCheck: {} as Record<string, { timestamp: number, status: string }>,
    statusCheckInProgress: {} as Record<string, boolean>,
    // New state for booking response
    lastBookingResponse: null as TableBookingResponse | null
  }),

  getters: {
    hasSelectedDateTime: (state) => !!state.selectedDate && !!state.selectedTime,
    formattedDate: (state) => state.selectedDate ? formatApiDate(state.selectedDate) : '',
    formattedDisplayDate: (state) => state.selectedDate ? dateUtils.formatBookingDateStandalone(state.selectedDate) : '',
    isBookingExpired: (state) => {
      if (!state.expirationTime) return false
      return new Date() > state.expirationTime
    },
    timeRemaining: (state) => {
      if (!state.expirationTime) return 0
      return Math.max(0, state.expirationTime.getTime() - Date.now())
    },
    // Check if we have a valid cache for any party size
    isCacheValid: (state) => {
      return state.masterAvailabilityCache.timestamp > 0 &&
             (Date.now() - state.masterAvailabilityCache.timestamp) < CACHE_DURATION
    },
    // Get availability for a specific party size from the cache
    getAvailabilityForPartySize: (state) => (partySize: number) => {
      if (state.masterAvailabilityCache.timestamp <= 0 ||
          (Date.now() - state.masterAvailabilityCache.timestamp) >= CACHE_DURATION) return null
      return state.masterAvailabilityCache.data[partySize] || []
    }
  },

  actions: {
    // Add a simple setter method
    setPlaceId(id: string) {
      if (id) this.placeId = id
    },

    // Fetch availability data for all party sizes once
    async fetchAllAvailabilityData() {
      // If we have valid cached data within the cache duration, use it for better performance
      if (this.masterAvailabilityCache.timestamp > 0 &&
          (Date.now() - this.masterAvailabilityCache.timestamp) < CACHE_DURATION) {
        return this.masterAvailabilityCache.data
      }

      this.loadingAvailableDates = true
      this.error = null

      try {
        // Use placeId from state instead of constant
        const response = await bookingService.getTablesAvailability(this.placeId, this.countryCode)

        // Extract maxPartySize from the API response if available
        if (response._rawResponse && response._rawResponse.config && response._rawResponse.config.maxPartySize) {
          this.maxPartySize = response._rawResponse.config.maxPartySize
        }

        // The response is now directly in the format we need - a record with party sizes as keys
        // and arrays of DateAvailability as values
        const availabilityData = response

        // Update the master cache
        this.masterAvailabilityCache = {
          data: availabilityData,
          timestamp: Date.now()
        }

        return availabilityData
      } catch (error) {
        this.error = 'Failed to load availability'
        console.error('Error fetching availability data:', error instanceof Error ? error.message : 'Unknown error')
        return {}
      } finally {
        this.loadingAvailableDates = false
      }
    },

    // Set the current party size and update available dates without unnecessary API calls
    async setPartySize(size: number) {
      this.partySize = size

      // Check if we have valid cached data before making any API calls
      const isCacheValid = this.masterAvailabilityCache.timestamp > 0 &&
                         (Date.now() - this.masterAvailabilityCache.timestamp) < CACHE_DURATION

      // Only fetch new data if cache is invalid or if this is the first time setting party size
      if (!isCacheValid) {
        await this.fetchAllAvailabilityData()
      }

      // Update the displayed availability based on the data (either from cache or newly fetched)
      // This ensures the UI shows availability for the selected party size
      await this.updateAvailableDatesForPartySize(size)
    },

    // Update displayed availability based on the selected party size
    async updateAvailableDatesForPartySize(partySize: number) {
      // Show we're processing
      this.isIncrementalLoading = true

      try {
        // Get master data if not already cached
        if (!(this.masterAvailabilityCache.timestamp > 0 &&
            (Date.now() - this.masterAvailabilityCache.timestamp) < CACHE_DURATION)) {
          await this.fetchAllAvailabilityData()
        }

        // Get available party sizes from cache
        const availablePartySizes = Object.keys(this.masterAvailabilityCache.data).map(Number)

        if (availablePartySizes.length === 0) {
          console.warn('No party sizes available in cache, keeping current data')
          this.error = 'No availability data found'
          return
        }

        // Find the closest available party size if exact match not available
        // This ensures we always show some availability data even if the exact party size isn't cached
        let dataPartySize = partySize
        if (!availablePartySizes.includes(partySize)) {
          // Default to closest size to provide best possible availability data
          dataPartySize = availablePartySizes.reduce((prev, curr) =>
            Math.abs(curr - partySize) < Math.abs(prev - partySize) ? curr : prev
          )
        }

        // Update the available dates from the cache for the selected party size
        const datesForPartySize = this.masterAvailabilityCache.data[dataPartySize] || []

        if (datesForPartySize.length === 0) {
          this.error = `No availability found for party size ${partySize}`
          console.warn(`No dates available for party size ${dataPartySize}`)
        }

        // Update the UI with the dates for the selected party size
        this.availableDates = [...datesForPartySize]
      } catch (error) {
        console.error('Error updating available dates:', error instanceof Error ? error.message : 'Unknown error')
        this.error = 'Failed to update available dates'
      } finally {
        this.isIncrementalLoading = false
      }
    },

    selectTime(time: string) {
      this.selectedTime = time
    },

    selectDate(date: Date) {
      this.selectedDate = date
    },

    setGuestInfo(info: GuestInfo) {
      this.guestInfo = info
    },

    async createBooking(): Promise<TableBookingResponse|void> {
      const { selectedDate, selectedTime, guestInfo, partySize } = this

      if (!selectedDate || !selectedTime || !guestInfo) {
        this.error = 'Missing booking information'
        console.error('Store: Missing booking information')
        return
      }

      // Prevent multiple submissions to avoid duplicate bookings
      if (this.isSubmittingBooking) {
        return
      }

      this.isLoading = true
      this.isSubmittingBooking = true
      this.error = null

      try {
        const response = await bookingService.createBooking({
          placeId: this.placeId,
          date: selectedDate,
          time: selectedTime,
          partySize,
          guestInfo,
          countryCode: this.countryCode
        })

        this.temporaryBooking = {
          id: String(Date.now()),
          date: selectedDate.toISOString(),
          time: selectedTime,
          partySize,
          guestInfo,
          status: 'pending',
          expiresAt: new Date(Date.now() + (response.ttl || 15 * 60 * 1000)).toISOString()
        }

        return response
      }
      catch (error) {
        console.error('Booking creation error:', error instanceof Error ? error.message : 'Unknown error')
        this.error = 'Failed to create booking'
      }
      finally {
        this.isLoading = false
        this.isSubmittingBooking = false
      }
    },

    /**
     * Clear the availability cache
     * @param force If true, always clear the cache. If false, only clear if expired.
     * @returns True if cache was cleared, false if it was still valid and not forced
     */
    clearCache(force: boolean = false): boolean {
      const isCacheValid = this.masterAvailabilityCache.timestamp > 0 &&
                         (Date.now() - this.masterAvailabilityCache.timestamp) < CACHE_DURATION

      // Only clear if forced or cache is invalid to maintain performance
      if (force || !isCacheValid) {
        this.masterAvailabilityCache = {
          data: {},
          timestamp: 0
        }
        return true
      }

      // Cache is still valid, no need to clear
      return false
    },

    resetBooking() {
      // Store the current placeId before resetting
      const currentPlaceId = this.placeId

      this.selectedDate = null
      this.selectedTime = null
      this.partySize = 2
      this.availableTimes = []
      this.availableDates = []
      this.partialAvailableDates = []
      this.guestInfo = null
      this.temporaryBooking = null
      this.expirationTime = null
      this.error = null
      // We don't clear the cache here to keep it available for future bookings

      // Restore the placeId to ensure it's preserved
      if (currentPlaceId) {
        this.placeId = currentPlaceId
      }
    },

    // Add a method to check if current selection is still valid after party size change
    checkSelectionValidityForNewPartySize(size: number, currentDate: Date | null, currentTime: string | null) {
      if (!currentDate || !currentTime) return false

      // Check if cache is valid directly instead of using the getter
      const isCacheValid = this.masterAvailabilityCache.timestamp > 0 &&
                         (Date.now() - this.masterAvailabilityCache.timestamp) < CACHE_DURATION

      if (!isCacheValid) {
        return false
      }

      // Check if a specific date and time are available for a given party size
      const isAvailable = (date: Date, time: string, partySize: number) => {
        if (!date || !time) return false

        // Get available party sizes from cache
        const availablePartySizes = Object.keys(this.masterAvailabilityCache.data).map(Number)

        if (availablePartySizes.length === 0) return false

        // Find the closest available party size if exact match not available
        let dataPartySize = partySize
        if (!availablePartySizes.includes(partySize)) {
          // Default to closest size
          dataPartySize = availablePartySizes.reduce((prev, curr) =>
            Math.abs(curr - partySize) < Math.abs(prev - partySize) ? curr : prev
          )
        }

        // Get the dates for this party size
        const datesForPartySize = this.masterAvailabilityCache.data[dataPartySize] || []

        // Find the matching date
        const matchingDate = datesForPartySize.find(d => {
          const availDate = new Date(d.date)
          return availDate.toDateString() === date.toDateString()
        })

        // Check if the time is available on this date
        return matchingDate?.availableTimes.includes(time) || false
      }

      return isAvailable(currentDate, currentTime, size)
    },

    // Add a method to check if date has any available times after party size changes
    checkDateHasAvailabilityForPartySize(size: number, date: Date) {
      if (!date) return false

      // Check if cache is valid
      const isCacheValid = this.masterAvailabilityCache.timestamp > 0 &&
                         (Date.now() - this.masterAvailabilityCache.timestamp) < CACHE_DURATION

      if (!isCacheValid) {
        return false
      }

      // Get available party sizes from cache
      const availablePartySizes = Object.keys(this.masterAvailabilityCache.data).map(Number)

      if (availablePartySizes.length === 0) return false

      // Find the closest available party size if exact match not available
      // This ensures we can still validate selections even with approximate data
      let dataPartySize = size
      if (!availablePartySizes.includes(size)) {
        // Default to closest size to provide best possible availability data
        dataPartySize = availablePartySizes.reduce((prev, curr) =>
          Math.abs(curr - size) < Math.abs(prev - size) ? curr : prev
        )
      }

      // Format date to string for comparison
      const dateString = formatApiDate(date)

      // Find the date in the cache
      const datesForPartySize = this.masterAvailabilityCache.data[dataPartySize] || []
      const dateInfo = datesForPartySize.find(d => d.date === dateString)

      // Return true if the date has availability
      return dateInfo?.hasAvailability || false
    },

    isDateAvailable(date: Date): boolean {
      const matchingDate = this.availableDates.find(d =>
        new Date(d.date).toDateString() === date.toDateString()
      )
      return matchingDate?.hasAvailability ?? false
    },

    // Get available times for a specific date, handling cache and API calls
    async getAvailableTimesForDate(date: Date): Promise<string[]> {
      // First check if we have times in the store
      const selectedDateInfo = this.availableDates.find(d => {
        const availDate = new Date(d.date)
        return availDate.toDateString() === date.toDateString()
      })

      if (selectedDateInfo && selectedDateInfo.availableTimes && selectedDateInfo.availableTimes.length > 0) {
        return selectedDateInfo.availableTimes
      }

      // If not in cache or empty, fetch fresh data
      await this.fetchAllAvailabilityData()

      // Find the date in the newly fetched data
      const dateInfo = this.availableDates.find(d => {
        const availDate = new Date(d.date)
        return availDate.toDateString() === date.toDateString()
      })

      return dateInfo?.availableTimes || []
    },

    // Add a method to clear the booking state when navigating away
    clearBookingState() {
      // Store the current placeId before clearing
      const currentPlaceId = this.placeId

      this.temporaryBooking = null
      this.expirationTime = null
      this.error = null
      this.lastBookingResponse = null

      // Restore the placeId to ensure it's preserved
      if (currentPlaceId) {
        this.placeId = currentPlaceId
      }
    },

    /**
     * Set the last booking API response
     */
    setLastBookingResponse(response: TableBookingResponse) {
      this.lastBookingResponse = response

      // If TTL is provided, calculate expiration time
      if (response.ttl) {
        this.expirationTime = new Date(Date.now() + response.ttl)
      } else {
        this.expirationTime = null
      }
    }
  }
})
